import { ref, computed, watch } from "vue";
import { arePricelistPricesEnabled, inventoryAccountingAppliesTo, Store, isSellingPriceLocked, I18n, TableSessionService, Product, isInventoryAccountingActive, ProductType, PrintRoleType, minSellingPrice, TaxRate, Pricelist, ProductCategory, Money, connectionService, ProductIngredient } from "app-core";
import { useForm } from "./index53.js";
import { object, custom } from "./index101.js";
function req(value) {
  if (Array.isArray(value)) return !!value.length;
  if (value === void 0 || value === null) return false;
  if (value === false) return true;
  if (value instanceof Date) return !isNaN(value.getTime());
  if (typeof value === "object") {
    for (const _ in value) return true;
    return false;
  }
  return !!String(value).length;
}
function minValueRule(value, min) {
  if (!req(value)) return true;
  return !/\s/.test(String(value)) && Number(value) >= min;
}
function createProductSchema() {
  return object({
    id: custom(),
    name: custom(),
    code: custom(),
    barcode: custom(),
    gtin: custom(),
    is_goods: custom(),
    is_measured: custom(),
    is_made: custom(),
    is_sold: custom(),
    excluded_from_delivery: custom(),
    description: custom(),
    image_url: custom(),
    dietary_tags: custom(),
    alcohol_percentage: custom(),
    requires_quantity: custom(),
    category_id: custom(),
    category: custom(),
    type: custom(),
    printing_sector: custom(),
    selling_price: custom(),
    tax_rate_id: custom(),
    count: custom(),
    selling_count: custom(),
    unit_of_measure: custom(),
    unit_of_measure_sold: custom(),
    net_quantity: custom(),
    prices: custom(),
    is_ingredient: custom(),
    ingredients: custom(),
    purchase_price: custom(),
    price_difference: custom(),
    min_count: custom()
  }).superRefine((data, ctx) => {
    const issue = (path, message) => ctx.addIssue({ code: "custom", message, path });
    const requiredIssue = (path) => issue(path, I18n.t("validation.required"));
    const isRetail = !!Store.get("isRetail");
    const iaEnabled = inventoryAccountingAppliesTo(data);
    const priceRequired = data.is_sold && !iaEnabled;
    const priceMin = minSellingPrice(data);
    if (!req(data.name)) requiredIssue(["name"]);
    if (req(data.name) && String(data.name).length > 200) issue(["name"], I18n.t("validation.max_length", { value: 200 }));
    if (data.is_measured && !req(data.code)) requiredIssue(["code"]);
    if (req(data.gtin) && String(data.gtin).length < 8) issue(["gtin"], I18n.t("validation.min_length", { value: 8 }));
    if (req(data.gtin) && String(data.gtin).length > 14) issue(["gtin"], I18n.t("validation.max_length", { value: 14 }));
    if (!isRetail && !req(data.type)) requiredIssue(["type"]);
    if (!isRetail && !req(data.category)) requiredIssue(["category"]);
    if (!isRetail && !req(data.printing_sector)) requiredIssue(["printing_sector"]);
    if (priceRequired && !req(data.selling_price)) requiredIssue(["selling_price"]);
    if (data.is_sold && !minValueRule(data.selling_price, priceMin)) issue(["selling_price"], I18n.t("validation.min_value", { value: priceMin }));
    if (data.is_sold && !data.is_made && !req(data.selling_count)) requiredIssue(["selling_count"]);
    if (!data.is_made && !iaEnabled && !req(data.count)) requiredIssue(["count"]);
    const mc = data.min_count;
    if (!(mc === null || mc === "" || isNaN(Number(mc))) && !minValueRule(mc, 0)) {
      issue(["min_count"], I18n.t("validation.min_value", { value: 0 }));
    }
    if (!req(data.unit_of_measure)) requiredIssue(["unit_of_measure"]);
    if (!req(data.unit_of_measure_sold)) requiredIssue(["unit_of_measure_sold"]);
    if (!req(data.tax_rate_id)) requiredIssue(["tax_rate_id"]);
    (data.is_sold ? data.prices || [] : []).forEach((p, index) => {
      if (!minValueRule(p.price, priceMin)) issue(["prices", index, "price"], I18n.t("validation.min_value", { value: priceMin }));
    });
  });
}
function useProductForm(product, options) {
  var _a;
  const endpointBase = (options == null ? void 0 : options.endpointBase) ?? "";
  const schema = createProductSchema();
  const tenant = () => Store.get("tenant");
  const isRetail = () => !!Store.get("isRetail");
  const appConfig = () => Store.get("appConfig");
  const productsList = () => (options == null ? void 0 : options.products) ?? Product.all();
  const enablePricelists = arePricelistPricesEnabled();
  const getUnitsOfMeasure = () => {
    var _a2, _b;
    const predefinedUnits = ((_b = (_a2 = appConfig()) == null ? void 0 : _a2.product) == null ? void 0 : _b.unitsOfMeasure) || [];
    const selectedUnits = productsList().map((p) => p.unit_of_measure);
    const allUnits = [...predefinedUnits, ...selectedUnits].filter((unit, index, array) => index === array.indexOf(unit));
    return allUnits.map((unit) => ({
      key: unit,
      value: I18n.t(`products.unit_of_measure.${unit}`) !== `products.unit_of_measure.${unit}` ? I18n.t(`products.unit_of_measure.${unit}`) : unit
    }));
  };
  const unitsOfMeasure = ref(getUnitsOfMeasure());
  function createUnitOfMeasure(unit) {
    form.data.unit_of_measure = unit;
    unitsOfMeasure.value.unshift({ key: unit, value: unit });
  }
  function buildFormData(product2) {
    var _a2, _b, _c, _d, _e, _f;
    const codes = productsList().filter((p) => {
      return p.code && !isNaN(Number(p.code)) && Number.isInteger(Number(p.code)) && !isNaN(parseInt(p.code, 10));
    }).map((p) => p.code);
    const nextCode = codes.length ? "" + (Math.max(...codes.map((c) => Number(c))) + 1) : "";
    const iaTenant = isInventoryAccountingActive();
    const defaultPrice = iaTenant ? "0" : "";
    const defaultTaxRateId = TaxRate.productTaxRates().length === 1 ? ((_a2 = TaxRate.productTaxRates()[0]) == null ? void 0 : _a2.id) || void 0 : void 0;
    return {
      id: (product2 == null ? void 0 : product2.id) || void 0,
      name: (product2 == null ? void 0 : product2.name) || "",
      code: (product2 == null ? void 0 : product2.id) ? product2.code || "" : nextCode,
      barcode: (product2 == null ? void 0 : product2.id) ? product2.barcode || "" : "",
      gtin: (product2 == null ? void 0 : product2.id) ? product2.gtin || "" : "",
      is_goods: product2 ? product2.is_goods === null || !!product2.is_goods : true,
      is_measured: product2 ? product2.is_measured : false,
      is_sold: product2 ? product2.is_sold : true,
      is_made: product2 ? product2.is_made : false,
      requires_quantity: product2 ? product2.requires_quantity : false,
      excluded_from_delivery: product2 ? product2.excluded_from_delivery : false,
      description: product2 ? product2.description || null : null,
      image_url: product2 ? product2.image_url || null : null,
      dietary_tags: ((_b = product2 == null ? void 0 : product2.dietary_tags) == null ? void 0 : _b.length) ? [...product2.dietary_tags] : null,
      alcohol_percentage: product2 ? product2.alcohol_percentage : null,
      selling_price: product2 && product2.selling_price != null ? product2.getSellingPrice().toFormat("0.00") : defaultPrice,
      tax_rate_id: product2 ? product2.tax_rate_id || void 0 : defaultTaxRateId,
      type: product2 ? product2.type || void 0 : void 0,
      category_id: product2 ? product2.category_id || void 0 : void 0,
      category: product2 ? ((_c = options == null ? void 0 : options.productCategories) == null ? void 0 : _c.find((c) => c.id === product2.category_id)) ?? ((_d = ProductCategory.find(product2.category_id)) == null ? void 0 : _d.$toJson()) : void 0,
      printing_sector: product2 ? product2.printing_sector || void 0 : appConfig() && !isRetail() ? appConfig().product.printingSectors[0] : void 0,
      selling_count: product2 ? "" + product2.getSellingCount() : "1",
      count: product2 ? "" + (parseFloat(product2.count) || 0) : "0",
      unit_of_measure: product2 ? product2.getUnitOfMeasure() || void 0 : ((_e = unitsOfMeasure.value[0]) == null ? void 0 : _e.value) || void 0,
      unit_of_measure_sold: product2 ? product2.getUnitOfMeasureSold() || void 0 : ((_f = unitsOfMeasure.value[0]) == null ? void 0 : _f.value) || void 0,
      net_quantity: product2 && product2.net_quantity ? "" + parseFloat(product2.net_quantity) : "",
      prices: !enablePricelists ? [] : ((options == null ? void 0 : options.pricelists) ?? Pricelist.all()).map((p) => {
        const existingPrice = product2 == null ? void 0 : product2.prices.find((price) => price.id === p.id);
        return {
          id: p.id,
          name: p.name,
          price: (existingPrice == null ? void 0 : existingPrice.price) ? Money.parse(existingPrice.price).toFormat("0.00") : ""
        };
      }),
      is_ingredient: product2 ? !product2.is_sold : false,
      ingredients: [],
      // Admin superset — old-admin defaults (0 under inventory accounting, null otherwise).
      purchase_price: product2 && product2.purchase_price != null ? "" + parseFloat(product2.purchase_price) : iaTenant ? "0" : null,
      price_difference: product2 && product2.price_difference != null ? "" + parseFloat(product2.price_difference) : "0",
      min_count: product2 && product2.min_count != null && !isNaN(parseFloat(product2.min_count)) ? "" + parseFloat(product2.min_count) : null
    };
  }
  const form = useForm(schema, buildFormData(product));
  const netQuantity = ref(form.data.net_quantity ? parseFloat(form.data.net_quantity) * 1e3 + "" : "");
  const inventoryAccountingEnabled = computed(() => inventoryAccountingAppliesTo(form.data));
  const showCount = computed(() => {
    var _a2, _b;
    return !((_b = (_a2 = tenant()) == null ? void 0 : _a2.venue) == null ? void 0 : _b.settings.hide_product_quantities) && !form.data.is_made && form.data.is_goods;
  });
  const showMinCount = computed(() => showCount.value);
  const showPurchasePrice = computed(() => inventoryAccountingEnabled.value);
  const showPriceDifference = computed(() => inventoryAccountingEnabled.value && !form.data.is_ingredient);
  const disableSellingPriceEdit = computed(() => isSellingPriceLocked(form.data));
  const disablePurchasePrice = computed(() => {
    if (inventoryAccountingEnabled.value && !form.data.is_ingredient && !form.data.is_made) return true;
    return !inventoryAccountingEnabled.value || form.data.is_made;
  });
  const disablePriceDifference = computed(() => inventoryAccountingEnabled.value);
  const showNetQuantity = computed(() => {
    if (!isRetail()) return false;
    if (!form.data.unit_of_measure) return false;
    return ["kg", "l"].includes(form.data.unit_of_measure.toLowerCase());
  });
  const netQuantityUnitOfMeasure = computed(() => {
    var _a2, _b;
    if (((_a2 = form.data.unit_of_measure) == null ? void 0 : _a2.toLowerCase()) === "kg") return "g";
    if (((_b = form.data.unit_of_measure) == null ? void 0 : _b.toLowerCase()) === "l") return "ml";
    return "";
  });
  const showUnitOfMeasureSold = computed(() => {
    if (!isRetail()) return false;
    if (!form.data.unit_of_measure) return false;
    return !["pcs", I18n.t("products.unit_of_measure.pcs")].includes(form.data.unit_of_measure.toLowerCase());
  });
  const qrOrderingEnabled = TableSessionService.isQrOrderingEnabled();
  const deliveryEnabled = !!((_a = Store.get("modules")) == null ? void 0 : _a.delivery);
  const showDigitalMenu = computed(() => !!form.data.is_sold && (qrOrderingEnabled || deliveryEnabled));
  const showDietaryTags = computed(() => qrOrderingEnabled);
  const showDeliveryFields = computed(() => deliveryEnabled);
  const availableIngredients = computed(() => {
    return productsList().filter((p) => {
      return p.id !== form.data.id && !p.is_made && !p.is_sold;
    });
  });
  watch(() => form.data.is_goods, (val) => {
    var _a2;
    if (!val) {
      form.data.barcode = "";
      form.data.gtin = "";
      form.data.is_measured = false;
      form.data.is_ingredient = false;
      form.data.is_made = false;
      form.data.requires_quantity = false;
      form.data.selling_price = void 0;
      (_a2 = form.data.prices) == null ? void 0 : _a2.forEach((p) => p.price = "");
    }
    form.validate();
  });
  watch(() => form.data.is_ingredient, (val) => {
    var _a2, _b;
    form.data.is_sold = !val;
    if (val) {
      form.data.is_made = false;
      form.data.requires_quantity = false;
      form.data.selling_price = void 0;
      form.data.price_difference = null;
      (_a2 = form.data.prices) == null ? void 0 : _a2.forEach((p) => p.price = "");
    } else {
      if (inventoryAccountingEnabled.value) {
        form.data.selling_price = product && product.selling_price != null ? product.getSellingPrice().toFormat("0.00") : "0";
        form.data.price_difference = product && product.price_difference != null ? "" + parseFloat(product.price_difference) : "0";
      } else {
        const defaultPrice = isInventoryAccountingActive() ? "0" : void 0;
        form.data.selling_price = product && product.selling_price != null ? product.getSellingPrice().toFormat("0.00") : defaultPrice;
        form.data.purchase_price = null;
      }
      (_b = form.data.prices) == null ? void 0 : _b.forEach((p) => {
        const existingPrice = product == null ? void 0 : product.prices.find((price) => price.id === p.id);
        p.price = (existingPrice == null ? void 0 : existingPrice.price) || "";
      });
    }
    form.data.selling_count = val || form.data.is_made ? void 0 : "1";
    form.validate();
  });
  watch(() => form.data.is_made, (val) => {
    if (val) {
      form.data.is_ingredient = false;
      form.data.count = "";
      if (inventoryAccountingEnabled.value) {
        form.data.purchase_price = "0";
        form.data.price_difference = "0";
      } else {
        form.data.purchase_price = null;
        form.data.price_difference = null;
      }
    } else {
      form.data.count = "" + (product == null ? void 0 : product.getCount()) || "0";
      if (inventoryAccountingEnabled.value) {
        form.data.purchase_price = product && product.purchase_price != null ? "" + parseFloat(product.purchase_price) : "0";
        form.data.price_difference = product && product.price_difference != null ? "" + parseFloat(product.price_difference) : "0";
      }
    }
    form.data.selling_count = val || form.data.is_ingredient ? void 0 : "1";
    form.validate();
  });
  watch(() => form.data.unit_of_measure, (val) => {
    if (!val || !["kg", "l"].includes(val)) {
      form.data.net_quantity = "";
      netQuantity.value = "";
    }
    form.data.unit_of_measure_sold = val;
  });
  watch(() => form.data.category, (val) => {
    form.data.category_id = (val == null ? void 0 : val.id) || void 0;
    form.data.type = (val == null ? void 0 : val.type) || void 0;
    if ((val == null ? void 0 : val.type) === ProductType.food) {
      form.data.printing_sector = PrintRoleType.kitchen;
    } else if ((val == null ? void 0 : val.type) === ProductType.drinks) {
      form.data.printing_sector = PrintRoleType.bar;
    }
    form.validate();
  });
  function validateField(field) {
    const result = schema.safeParse(form.data);
    form.clearErrors(field);
    if (!result.success) {
      form.errors.value.push(...result.error.issues.filter((issue) => issue.path.join(".") === field).map((issue) => ({ name: field, message: issue.message })));
    }
  }
  async function save() {
    form.data.net_quantity = netQuantity.value ? Money.parse(netQuantity.value).divide(1e3).toFormat("0.000") : "";
    if (form.validate().length) return { status: "invalid" };
    if (!connectionService.isOnline) return { status: "offline" };
    if (!form.data.is_ingredient) {
      const usedAsIngredient = ProductIngredient.all().find((i) => i.product_id === form.data.id) || productsList().flatMap((p) => p.ingredients || []).map((i) => i.pivot).find((pivot) => pivot && pivot.product_id === form.data.id);
      if (usedAsIngredient) return { status: "in_use" };
    }
    if (!form.data.unit_of_measure_sold) {
      form.data.unit_of_measure_sold = form.data.unit_of_measure;
    }
    if (!inventoryAccountingEnabled.value) {
      form.data.purchase_price = null;
    }
    if (form.data.min_count === "") {
      form.data.min_count = null;
    }
    const previousIngredients = (product == null ? void 0 : product.id) ? product.getIngredients() : [];
    try {
      const response = !form.data.id ? await form.post(`${endpointBase}/products`) : await form.patch(`${endpointBase}/products/${form.data.id}`);
      Product.insert({ data: [response.data] });
      if (response.data.category) {
        ProductCategory.insert({ data: [response.data.category] });
      }
      previousIngredients.forEach((i) => i.$delete());
      if (response.data.ingredients) {
        ProductIngredient.insert({ data: response.data.ingredients.map((i) => i.pivot).flat() });
      }
      return { status: "saved", product: new Product(response.data), raw: response.data };
    } catch {
      return { status: "error" };
    }
  }
  return {
    form,
    schema,
    netQuantity,
    unitsOfMeasure,
    createUnitOfMeasure,
    enablePricelists,
    inventoryAccountingEnabled,
    showCount,
    showMinCount,
    showPurchasePrice,
    showPriceDifference,
    disableSellingPriceEdit,
    disablePurchasePrice,
    disablePriceDifference,
    showNetQuantity,
    netQuantityUnitOfMeasure,
    showUnitOfMeasureSold,
    showDigitalMenu,
    showDietaryTags,
    showDeliveryFields,
    availableIngredients,
    validateField,
    save
  };
}
export {
  createProductSchema,
  useProductForm
};
//# sourceMappingURL=index41.js.map
